'use client'

import React from 'react'
import Link from 'next/link'
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Typography, alpha } from '@mui/material'

export interface AccountsSidePanelItemView {
    index: number
    label: string
    path: string
    icon: React.ReactElement
}

export interface AccountsSidePanelProps {
    items: AccountsSidePanelItemView[]
    activeIndex: number
    locale: string
}

const AccountsSidePanel: React.FC<AccountsSidePanelProps> = ({ items, activeIndex, locale }) => {
    return (
        <Box
            sx={{
                width: 260,
                flexShrink: 0,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                p: 2,
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <List
                component="nav"
                sx={{
                    p: 0,
                    '& .MuiListItemButton-root': {
                        borderRadius: 1.5,
                        mb: 0.25
                    }
                }}
            >
                {items.map(item => {
                    const isActive = item.index === activeIndex

                    return (
                        <ListItemButton
                            key={item.index}
                            component={Link}
                            href={`/${locale}${item.path}`}
                            selected={isActive}
                            aria-current={isActive ? 'page' : undefined}
                            sx={{
                                px: 1.5,
                                py: 2,
                                '&.Mui-selected': theme => ({
                                    bgcolor: alpha(theme.palette.link.main, 0.08),
                                    color: alpha(theme.palette.link.main, 0.9),
                                    '& .MuiListItemIcon-root': {
                                        color: alpha(theme.palette.link.main, 0.9)
                                    },
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.link.main, 0.12)
                                    }
                                })
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 32,
                                    color: isActive ? theme => alpha(theme.palette.link.main, 0.9) : 'text.secondary'
                                }}
                            >
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: isActive ? 600 : 500,
                                                fontSize: '0.9rem'
                                        }}
                                    >
                                        {item.label}
                                    </Typography>
                                }
                            />
                        </ListItemButton>
                    )
                })}
            </List>
        </Box>
    )
}

export { AccountsSidePanel }

